import {Grid, Typography} from "@material-ui/core";
import {DatagridProps, NumberField, useListContext, useResourceContext} from "react-admin";
import {useQuery} from "react-query";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import {useEffect, useState} from "react";
import DoneAllIcon from '@material-ui/icons/DoneAll';
import MoneyOffIcon from '@material-ui/icons/MoneyOff';
import {client, getRealResource} from "../../dataProvider";
import {StatCard} from "../../components/StatCard";
import {useListQueryParams} from "../../shared/hooks/useListQueryParams";

export const InvoiceTimeTableListHeader = (
    props: DatagridProps & { passive?: boolean; type?: any }
) => {
    const passive = props.passive || false;
    const tabType = props.type || "S";
    const isAll = tabType === "T";

    const resource = useResourceContext();
    const query = useListQueryParams();
    const {filterValues, filter, ids, data, selectedIds} = useListContext(props);

    const [manualStats, setManualStats] = useState({
        amount_selected: 0,
        rest_selected: 0,
        total_selected: 0
    });

    useEffect(() => {
        if (selectedIds && data) {
            let tempsManualStats = {
                amount_selected: 0,
                rest_selected: 0,
                total_selected: 0
            };

            for (let sId of selectedIds) {
                const row = data[sId];
                if (!row) continue;

                const docSign = ( row.invoice?.type === "FT" || row.invoice?.type === "ND") ? 1 : -1;

                const passiveSign =
                    isAll && row.invoice?.invoice_type === "P" ? -1 : 1;

                const sign = docSign * passiveSign;

                tempsManualStats.amount_selected += sign * row.amount;
                tempsManualStats.rest_selected += sign * row.residue;
            }

            tempsManualStats.total_selected =
                tempsManualStats.amount_selected - tempsManualStats.rest_selected;

            setManualStats(tempsManualStats);
        }
    }, [selectedIds, data, isAll]);

    const url = `${getRealResource(resource)}/statistics`;

    const {data: statsFromApi} = useQuery(
        [url, query, filterValues, filter, ids],
        () =>
            client(url, {
                method: "GET",
                params: {
                    ...query,
                    ...filter,
                    ...filterValues,
                },
                paramsSerializer: function handleQuery(q: any) {
                    return Object.entries(q)
                        .map(([key, value]) =>
                            Array.isArray(value)
                                ? `${key}=${value.join('&' + key + '=')}`
                                : `${key}=${value}`
                        )
                        .join('&');
                }
            }),
        {
            initialData: {
                amount__sum: 0,
                rest__sum: 0,
                total__sum: 0
            },
        }
    );

    const stats = statsFromApi || {};

    const mainColor = isAll ? "black" : (passive ? "red" : "darkgreen");

    const subtitleTotale = isAll
        ? "Totale (attive - passive)"
        : passive
            ? "Totale da pagare"
            : "Totale da incassare";

    const subtitleResiduo = isAll
        ? "Residuo (attive - passive)"
        : passive
            ? "Residuo da pagare"
            : "Residuo da incassare";

    const subtitlePagato = isAll
        ? "Pagato (attive - passive)"
        : passive
            ? "Totale pagato"
            : "Totale incassato";

    return (
        <Grid container alignItems="center"
              style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'white',
                  zIndex: 1000,
              }}>
            <Grid item xs={12} sm={3}>
                <StatCard
                    label={
                        <NumberField
                            record={stats}
                            source="amount__sum"
                            variant="inherit"
                            options={{
                                style: "currency",
                                currency: "EUR",
                            }}
                            style={{color: mainColor}}
                        />
                    }
                    icon={<AttachMoneyIcon/>}
                    subtitle={subtitleTotale}
                    color={mainColor}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <StatCard
                    label={
                        <NumberField
                            record={stats}
                            source="rest__sum"
                            variant="inherit"
                            options={{
                                style: "currency",
                                currency: "EUR",
                            }}
                            style={{color: mainColor}}
                        />
                    }
                    icon={<MoneyOffIcon/>}
                    subtitle={subtitleResiduo}
                    color={mainColor}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <StatCard
                    label={
                        <NumberField
                            record={stats}
                            source="total__sum"
                            variant="inherit"
                            options={{
                                style: "currency",
                                currency: "EUR",
                            }}
                            defaultValue={"0,0"}
                            style={{color: mainColor}}
                        />
                    }
                    icon={<DoneAllIcon/>}
                    subtitle={subtitlePagato}
                    color={mainColor}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <Typography variant="h4"> &#60;- Totali</Typography>
            </Grid>

            {manualStats && (
                <Grid item container xs={12}>
                    <Grid item xs={3}>
                        <StatCard
                            label={
                                <NumberField
                                    record={{
                                        id: 0,
                                        amount_selected: manualStats.amount_selected
                                    }}
                                    source="amount_selected"
                                    variant="inherit"
                                    options={{
                                        style: "currency",
                                        currency: "EUR",
                                    }}
                                    style={{color: mainColor}}
                                />
                            }
                            icon={<AttachMoneyIcon/>}
                            subtitle={
                                isAll
                                    ? "Totale selezione (A - P)"
                                    : passive
                                        ? "Totale da pagare della selezione"
                                        : "Totale da incassare della selezione"
                            }
                            color={mainColor}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <StatCard
                            label={
                                <NumberField
                                    record={{
                                        id: 0,
                                        rest_selected: manualStats.rest_selected
                                    }}
                                    source="rest_selected"
                                    variant="inherit"
                                    options={{
                                        style: "currency",
                                        currency: "EUR",
                                    }}
                                    style={{color: mainColor}}
                                />
                            }
                            icon={<MoneyOffIcon/>}
                            subtitle={
                                isAll
                                    ? "Residuo selezione (A - P)"
                                    : passive
                                        ? "Resto da pagare della selezione"
                                        : "Resto da incassare della selezione"
                            }
                            color={mainColor}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <StatCard
                            label={
                                <NumberField
                                    record={{
                                        id: 0,
                                        total_selected: manualStats.total_selected
                                    }}
                                    source="total_selected"
                                    variant="inherit"
                                    options={{
                                        style: "currency",
                                        currency: "EUR",
                                    }}
                                    style={{color: mainColor}}
                                />
                            }
                            icon={<DoneAllIcon/>}
                            subtitle={
                                isAll
                                    ? "Netto selezione (A - P)"
                                    : passive
                                        ? "Pagato della selezione"
                                        : "Incassato della selezione"
                            }
                            color={mainColor}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="h4"> &#60;- Selezionate</Typography>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};
